import React, { useCallback, useEffect, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Platform, type View } from 'react-native';
import { IngredientCardV2, useUniqueNativeID } from '@sg/garnish';
import { type IngredientModification } from '@sg/graphql-schema';

import { IngredientModificationsTypes } from '@order/utils';

import { INGREDIENT_IMAGE_WIDTH } from '../../../../constants';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsIngredientCard = (
  props: ProductDetailsIngredientCardProps,
) => {
  const {
    dietaryRestrictionNote,
    ingredientModification,
    quantity,
    activeStepperId,
    isAtMaxQuantity,
    onIncrement,
    onDecrement,
    setActiveStepperId,
    resetStepperTimeout,
  } = props;

  const isOutOfStock = ingredientModification.outOfStock;

  const isDressing =
    ingredientModification.kind === IngredientModificationsTypes.Dressings;

  const { formatMessage } = useIntl();

  // ─── Refs ────────────────────────────────────────────────────────────

  const shouldUseTimeoutRef = useRef(false);
  const isKeyPressedRef = useRef(false);
  const cardRef = useRef<View>(null);

  // ─────────────────────────────────────────────────────────────────────

  const accessibilityLabel = formatMessage(messages.a11yCardLabel, {
    quantity,
    ingredient_name: ingredientModification.ingredient.name,
  });

  const stepperNativeId = useUniqueNativeID();

  // ─────────────────────────────────────────────────────────────────────

  const focusCard = useCallback(() => {
    if (Platform.OS !== 'web') return;

    try {
      cardRef.current?.focus?.();
    } catch {}
  }, []);

  const closeStepper = useCallback(() => {
    setActiveStepperId(null);
    focusCard();
  }, [setActiveStepperId, focusCard]);

  const handleIncrement = useCallback(() => {
    onIncrement(ingredientModification);

    if (shouldUseTimeoutRef.current) {
      resetStepperTimeout();
    }
  }, [ingredientModification, onIncrement, resetStepperTimeout]);

  const handleDecrement = useCallback(() => {
    onDecrement(ingredientModification);

    if (shouldUseTimeoutRef.current) {
      resetStepperTimeout();
    }
  }, [ingredientModification, onDecrement, resetStepperTimeout]);

  const handlePress = useCallback(
    (withTimeout: boolean) => {
      shouldUseTimeoutRef.current = withTimeout;
      isKeyPressedRef.current = false;

      // Close the stepper if the customer taps the same ingredient again.
      if (activeStepperId === ingredientModification.ingredient.id) {
        closeStepper();

        return;
      }

      // Do not use the stepper for dressings, these use a checkmark instead.
      if (isDressing && quantity === 0) {
        onIncrement(ingredientModification);

        return;
      }

      // When the dressing checkmark is already check, uncheck it.
      if (isDressing && quantity !== 0) {
        onDecrement(ingredientModification);

        return;
      }

      // Automatically increment modification if it is not selected, triggering the limitations banner.
      if (quantity === 0) {
        onIncrement(ingredientModification);
      }

      // Do not open the stepper for new modifications if they can't be added.
      if (quantity === 0 && isAtMaxQuantity) return;

      setActiveStepperId(ingredientModification.ingredient.id);

      if (shouldUseTimeoutRef.current) {
        resetStepperTimeout();
      }
    },
    [
      activeStepperId,
      ingredientModification,
      isDressing,
      quantity,
      isAtMaxQuantity,
      setActiveStepperId,
      closeStepper,
      onIncrement,
      onDecrement,
      resetStepperTimeout,
    ],
  );

  const onPress = useCallback(() => {
    const isKeyPressed = isKeyPressedRef.current;

    handlePress(!isKeyPressed);
  }, [handlePress]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const isEscapeKey = event?.code === 'Escape';
      const isUnrelatedKey = event?.code === 'Tab';
      const isSameTarget = event?.target === cardRef.current;

      if (isEscapeKey) {
        closeStepper();

        return;
      }

      if (!isSameTarget || isUnrelatedKey) {
        return;
      }

      isKeyPressedRef.current = true;
    },
    [closeStepper],
  );

  const isStepperActive =
    activeStepperId === ingredientModification.ingredient.id;

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isStepperActive) return;

    return () => {
      isKeyPressedRef.current = false;
    };
  }, [isStepperActive]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <IngredientCardV2.Container onKeyDown={onKeyDown}>
      <IngredientCardV2.Pressable
        ref={cardRef}
        disabled={isOutOfStock}
        onPress={onPress}
        accessibilityLabel={accessibilityLabel}
        aria-selected={quantity > 0}
        aria-expanded={isStepperActive}
        aria-controls={isStepperActive ? stepperNativeId : undefined}
        isNestedElementFocused={isStepperActive}
      />

      <IngredientCardV2.ImageContainer
        pointerEvents={isStepperActive ? 'auto' : 'none'}
      >
        <IngredientCardV2.Image
          source={ingredientModification.ingredient.asset.url}
          accessibilityLabel={ingredientModification.ingredient.name}
          imageWidth={INGREDIENT_IMAGE_WIDTH}
        />

        {isStepperActive ? (
          <IngredientCardV2.QuantityStepper.Container
            nativeID={stepperNativeId}
          >
            <IngredientCardV2.QuantityStepper.StepperButton
              variation="decrease"
              onPress={handleDecrement}
              accessibilityLabel={formatMessage(messages.a11yDecrementLabel)}
            />

            <IngredientCardV2.QuantityStepper.Label quantity={quantity} />

            <IngredientCardV2.QuantityStepper.StepperButton
              variation="increase"
              onPress={handleIncrement}
              accessibilityLabel={formatMessage(messages.a11yIncrementLabel)}
              autoFocusWeb={isStepperActive}
            />
          </IngredientCardV2.QuantityStepper.Container>
        ) : null}

        {!isStepperActive && quantity > 0 ? (
          <IngredientCardV2.Quantity shouldUseIcon={isDressing}>
            {quantity}
          </IngredientCardV2.Quantity>
        ) : null}
      </IngredientCardV2.ImageContainer>

      <IngredientCardV2.Label>
        {ingredientModification.ingredient.name}
      </IngredientCardV2.Label>

      {isOutOfStock ? (
        <IngredientCardV2.NoticeLabel>
          <FormattedMessage {...messages.unavailableLabel} />
        </IngredientCardV2.NoticeLabel>
      ) : null}

      {dietaryRestrictionNote && !isOutOfStock ? (
        <IngredientCardV2.NoticeLabel>
          {dietaryRestrictionNote}
        </IngredientCardV2.NoticeLabel>
      ) : null}
    </IngredientCardV2.Container>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  a11yCardLabel: {
    defaultMessage: `{quantity, select,
      0 {Add {ingredient_name}}
      other {Increase quantity of {ingredient_name} (current quantity: {quantity})}
    }`,
    description: 'Customization | Ingredient card | a11y label',
  },
  a11yIncrementLabel: {
    defaultMessage: 'Increase product quantity',
    description:
      'Customization | Ingredient card increment button | a11y label',
  },
  a11yDecrementLabel: {
    defaultMessage: 'Decrease product quantity',
    description:
      'Customization | Ingredient card decrement button | a11y label',
  },
  unavailableLabel: {
    defaultMessage: 'Unavailable',
    description: 'Customization | Ingredient card | Unavailable label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ProductDetailsIngredientCardProps = {
  dietaryRestrictionNote: string;
  ingredientModification: IngredientModification;
  quantity: number;
  palette?: 'kale' | 'spinach';
  activeStepperId: string | null;
  isAtMaxQuantity: boolean;
  onIncrement: (ingredientModification: IngredientModification) => void;
  onDecrement: (ingredientModification: IngredientModification) => void;
  setActiveStepperId: (id: string | null) => void;
  resetStepperTimeout: () => void;
};
